import React, { useState, useEffect } from 'react';
import ProtecaoAcesso from '../components/ProtecaoAcesso';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    TableCell as MuiTableCell,
    styled,
    tableCellClasses
} from '@mui/material';
import { PrimaryButton } from '../components';
import InfoHeader from '../components/InfoHeader';
import BasicMenu from '../components/BasicMenu';
import { metaService } from '../services/MetaService';
import Meta from '../types/negocio/Meta';
import toastService from '../utils/toastService';
import { colaboradorService } from '../services/ColaboradorService';
import Colaborador from '../types/negocio/Colaborador';
import MetaModal from '../components/modais/MetaModal';
import LoadingIndicator from '../components/LoadingIndicator';
import ProtecaoBotao from '../components/ProtecaoBotao';
import ProtectedBasicMenu from '../components/ProtectedBasicMenu';

const StyledTableCell = styled(MuiTableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: '#042E4B',
        color: theme.palette.common.white,
        fontWeight: 'bold'
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
    },
    '&:last-child td, &:last-child th': {
        border: 0,
    },
}));

const MetasPage: React.FC = () => {
    const [metas, setMetas] = useState<Meta[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [modalAberto, setModalAberto] = useState(false);
    const [modoEdicao, setModoEdicao] = useState(false);
    const [metaSelecionada, setMetaSelecionada] = useState<Meta>({} as Meta);
    const [colaboradores, setColaboradores] = useState<Colaborador[]>([]);

    const criarMetaVazia = (): Meta => ({
        meta: '',
        tipo: 'Valor',
        janeiroOrcado: 0,
        janeiroRealizado: 0,
        fevereiroOrcado: 0,
        fevereiroRealizado: 0,
        marcoOrcado: 0,
        marcoRealizado: 0,
        abrilOrcado: 0,
        abrilRealizado: 0,
        maioOrcado: 0,
        maioRealizado: 0,
        junhoOrcado: 0,
        junhoRealizado: 0,
        julhoOrcado: 0,
        julhoRealizado: 0,
        agostoOrcado: 0,
        agostoRealizado: 0,
        setembroOrcado: 0,
        setembroRealizado: 0,
        outubroOrcado: 0,
        outubroRealizado: 0,
        novembroOrcado: 0,
        novembroRealizado: 0,
        dezembroOrcado: 0,
        dezembroRealizado: 0,
        totalOrcado: 0,
        totalRealizado: 0
    });

    useEffect(() => {
        fetchMetas();
        fetchColaboradores();
    }, []);

    const fetchMetas = async () => {
        try {
            setIsLoading(true);
            setError(null);
            const data = await metaService.listarTodos();
            
            if (Array.isArray(data)) {
                setMetas(data);
            } else {
                console.error('Resposta inesperada ao carregar metas:', data);
                toastService.error('Formato de dados inesperado. Entre em contato com o suporte.');
                setMetas([]);
            }
        } catch (error) {
            console.error('Erro ao carregar metas:', error);
            setError("Não foi possível carregar os dados das metas. Por favor, tente novamente.");
            toastService.error('Erro ao carregar metas. Tente novamente mais tarde.');
        } finally {
            setIsLoading(false);
        }
    };

    const fetchColaboradores = async () => {
        try {
            const data = await colaboradorService.listarTodos();
            setColaboradores(data);
        } catch (error) {
            console.error('Erro ao carregar colaboradores:', error);
        }
    };

    const handleEdit = (index: number) => {
        setMetaSelecionada(metas[index]);
        setModoEdicao(true);
        setModalAberto(true);
    };

    const handleNovaMeta = () => {
        setMetaSelecionada(criarMetaVazia());
        setModoEdicao(true);
        setModalAberto(true);
    };

    const handleViewMore = (index: number) => {
        setMetaSelecionada(metas[index]);
        setModoEdicao(false);
        setModalAberto(true);
    };

    const handleSaveMeta = async (data: Meta) => {
        try {
            const isNovaMeta = !data.guid || data.guid.trim() === '';
            
            if (isNovaMeta) {
                await metaService.save(data);
                toastService.success('Meta criada com sucesso!');
            } else {
                await metaService.save(data);
                toastService.success('Meta atualizada com sucesso!');
            }
            
            setModalAberto(false);
            
            // Atualizar a lista
            const listaAtualizada = await metaService.listarTodos();
            setMetas(listaAtualizada);
        } catch (error) {
            console.error('Erro ao salvar meta:', error);
            toastService.error('Erro ao salvar meta.');
        }
    };

    const handleDelete = async (index: number) => {
        const metaToDelete = metas[index];

        if (!metaToDelete?.guid) {
            toastService.error("Meta não encontrada.");
            return;
        }

        try {
            await metaService.deletar(metaToDelete.guid);
            
            // Remover da lista local
            const metasRestantes = metas.filter((_, i) => i !== index);
            setMetas(metasRestantes);
            
            toastService.success("Meta excluída com sucesso!");
        } catch (error) {
            console.error("Erro ao excluir meta:", error);
            toastService.error("Erro ao excluir meta. Tente novamente.");
        }
    };

    const formatarValor = (valor?: number, tipo?: string) => {
        if (!valor) return '-';
        
        if (tipo === 'Valor') {
            return valor.toLocaleString('pt-BR', { 
                style: 'currency', 
                currency: 'BRL' 
            });
        } else if (tipo === 'Percentual') {
            return `${valor}%`;
        }
        return valor.toString();
    };

    const calcularPercentualRealizacao = (meta: Meta): string => {
        if (!meta.totalOrcado || meta.totalOrcado === 0) return '0%';
        const percentual = ((meta.totalRealizado || 0) / meta.totalOrcado) * 100;
        return `${percentual.toFixed(1)}%`;
    };

    const obterNomeResponsavel = (responsavelGuid?: string): string => {
        if (!responsavelGuid) return '-';
        const colaborador = colaboradores.find(c => c.guid === responsavelGuid);
        return colaborador?.nomeCompleto || '-';
    };

    // Show loading state when data is being fetched
    if (isLoading) {
        return <LoadingIndicator fullHeight message="Carregando metas..." />;
    }

    // Show error state only after loading is complete and there's an error
    if (error && !isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column', gap: 2 }}>
                <Typography color="error">{error}</Typography>
                <PrimaryButton onClick={() => globalThis.location.reload()}>
                    Tentar Novamente
                </PrimaryButton>
            </Box>
        );
    }

    return (
        <ProtecaoAcesso tela="metas">
            <InfoHeader
                title="Metas Específicas"
                message="Gerencie as metas da sua empresa com acompanhamento mensal do orçado versus realizado."
                isImportant
                modalTitle='Entenda a aba de Metas'
                urlTutorial='https://youtu.be/h1cWZnFSYcE'
                urlConceito='https://www.youtube.com/watch?v=KbUX0rsHHlQ'
            />

            <ProtecaoBotao tela="metas" tipo="alteracao">
                <PrimaryButton variant="contained" onClick={handleNovaMeta}>
                    + Adicionar
                </PrimaryButton>
            </ProtecaoBotao>

            <Paper elevation={3} sx={{ mt: 2 }}>
                <TableContainer sx={{ borderRadius: '8px' }}>
                    <Table aria-label="metas table" sx={{ minWidth: 800 }}>
                        <TableHead>
                            <TableRow>
                                <StyledTableCell><strong>Meta</strong></StyledTableCell>
                                <StyledTableCell><strong>Tipo</strong></StyledTableCell>
                                <StyledTableCell><strong>Responsável</strong></StyledTableCell>
                                <StyledTableCell align="center"><strong>Orçado</strong></StyledTableCell>
                                <StyledTableCell align="center"><strong>Realizado</strong></StyledTableCell>
                                <StyledTableCell align="center"><strong>% Realização</strong></StyledTableCell>
                                <StyledTableCell align="center"><strong>Ações</strong></StyledTableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {metas.length === 0 ? (
                                <StyledTableRow>
                                    <StyledTableCell colSpan={7} align="center">
                                        <Typography sx={{ color: 'text.secondary', py: 2 }}>
                                            Nenhuma meta cadastrada
                                        </Typography>
                                    </StyledTableCell>
                                </StyledTableRow>
                            ) : (
                                metas.map((meta, index) => (
                                    <StyledTableRow key={meta.guid || `meta-${index}`}>
                                        <StyledTableCell 
                                            component="th" 
                                            scope="row"
                                            onClick={() => handleViewMore(index)}
                                            sx={{ cursor: "pointer" }}
                                        >
                                            <Typography variant="body2" sx={{ fontSize: 16, "&:hover": { textDecoration: "underline", color: "primary.main" } }}>
                                                {meta.meta}
                                            </Typography>
                                        </StyledTableCell>
                                        <StyledTableCell>
                                            <Typography variant="body2" sx={{ fontSize: 16 }}>
                                                {meta.tipo}
                                            </Typography>
                                        </StyledTableCell>
                                        <StyledTableCell>
                                            <Typography variant="body2" sx={{ fontSize: 16 }}>
                                                {obterNomeResponsavel(meta.responsavelGuid)}
                                            </Typography>
                                        </StyledTableCell>
                                        <StyledTableCell align="center">
                                            <Typography variant="body2" sx={{ fontSize: 16 }}>
                                                {formatarValor(meta.totalOrcado, meta.tipo)}
                                            </Typography>
                                        </StyledTableCell>
                                        <StyledTableCell align="center">
                                            <Typography variant="body2" sx={{ fontSize: 16 }}>
                                                {formatarValor(meta.totalRealizado, meta.tipo)}
                                            </Typography>
                                        </StyledTableCell>
                                        <StyledTableCell align="center">
                                            <Typography 
                                                variant="body2" 
                                                sx={{ 
                                                    fontSize: 16,
                                                    color: (meta.totalRealizado || 0) >= (meta.totalOrcado || 0) ? 'success.main' : 'warning.main',
                                                    fontWeight: 'bold'
                                                }}
                                            >
                                                {calcularPercentualRealizacao(meta)}
                                            </Typography>
                                        </StyledTableCell>
                                        <StyledTableCell align="center">
                                            <ProtectedBasicMenu
                                                tela='metas'
                                                onEdit={() => handleEdit(index)}
                                                onDelete={() => handleDelete(index)}
                                                onViewMore={() => handleViewMore(index)}
                                            />
                                        </StyledTableCell>
                                    </StyledTableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            <MetaModal
                open={modalAberto}
                onClose={() => setModalAberto(false)}
                meta={metaSelecionada}
                modoEdicao={modoEdicao}
                setMeta={modoEdicao ? setMetaSelecionada : undefined}
                onSave={modoEdicao ? handleSaveMeta : undefined}
                onEditClick={() => setModoEdicao(true)}
                isNovaMeta={!metaSelecionada?.guid || metaSelecionada.guid.trim() === ''}
            />
        </ProtecaoAcesso>
    );
};

export default MetasPage;